import React, { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import { common } from "../../util/common";
import {
  convertTo12HourPlusDateFormat,
  formatToPeso,
} from "../../util/helperFunctions";
import RequestItemList from "./reusable/request-item-list";
import FormsSearch from "./reusable/forms-search";
import "./request-main.css";
import ReportItemList from "./reusable/report-item-list";
import FormsDropdown from "./reusable/forms-search-dropdown";

function ReportList() {
  const PAGE_SIZE = 8;

  const [requests, setRequests] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const [formsList, setFormsList] = useState([]);
  const [selectedForm, setSelectedForm] = useState("");

  const debounceRef = useRef(null);

  const mapRequests = (requests = []) => {
    return requests.map((request) => ({
      id: request.id,
      request_id: request.reference_id,
      request_type: request.request_type,
      link_id: request.request_link_id,
      total_cost: formatToPeso(request.total_cost),
      form_detail: request.form_detail,
      requester_user_detail: request.requester_user_detail,
      status:
        request.cancel_status === true
          ? "Cancelled"
          : request.reject_status
          ? "Denied"
          : request.overall_approval_status === true
          ? "Approved"
          : "Pending",
      date: convertTo12HourPlusDateFormat(request.createdAt),
    }));
  };

  const getFormsList = async () => {
    try {
      const response = await ApiService.forms.getForms();
      const { success, forms = [] } = response || {};

      if (success) {
        const formattedForms = forms.map((form) => ({
          label: form.name,
          value: form.id,
        }));
        setFormsList(formattedForms);
      } else {
        showSwal(common.errors.GETTING_FORMS_LIST_ERROR);
      }
    } catch (err) {
      console.error("Failed to fetch forms list:", err);
    }
  };

  const getReportList = async ({
    page = 1,
    pageSize = PAGE_SIZE,
    keyword = "",
    append = false,
  } = {}) => {
    try {
      if (append) {
        setIsFetchingMore(true);
      } else {
        setIsInitialLoading(true);
      }

      const response = await ApiService.forms.getReportListV2(
        page,
        pageSize,
        keyword,
        selectedForm
      );

      const { success, requests: apiRequests = [] } = response || {};

      if (!success) {
        showSwal(common.errors.GETTING_MY_REPORTS_ERROR);
        setHasMore(false);
        return;
      }

      const formattedRequests = mapRequests(apiRequests);

      setRequests((prev) => {
        if (!append) return formattedRequests;

        const existingIds = new Set(prev.map((item) => item.id));
        const dedupedNewItems = formattedRequests.filter(
          (item) => !existingIds.has(item.id),
        );

        return [...prev, ...dedupedNewItems];
      });

      setHasMore(apiRequests.length === pageSize);
      setPagination((prev) => ({
        ...prev,
        page,
        pageSize,
      }));
    } catch (error) {
      console.error("Failed to fetch reports:", error);
      showSwal(common.errors.GETTING_MY_REPORTS_ERROR);
      setHasMore(false);
    } finally {
      setIsInitialLoading(false);
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    getFormsList();
    getReportList({
      page: 1,
      pageSize: PAGE_SIZE,
      keyword: "",
      append: false,
    });
  }, [selectedForm]);

  const fetchMoreData = async () => {
    if (isFetchingMore || !hasMore || isInitialLoading) return;

    await getReportList({
      page: pagination.page + 1,
      pageSize: pagination.pageSize,
      keyword: searchKeyword,
      append: true,
    });
  };

  const handleSearch = (query) => {
    const trimmedQuery = query.trim();

    setSearchKeyword(trimmedQuery);
    setHasMore(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setRequests([]);
      getReportList({
        page: 1,
        pageSize: PAGE_SIZE,
        keyword: trimmedQuery,
        append: false,
      });
    }, 400);
  };

  return (
    <div className="request-main">
      <div className="request-main-title">
        <div className="request-main-title__top">
          <h3>Requests Report List</h3>
        </div>

        <div className="request-main-title__search">
          <FormsSearch handleSearch={handleSearch} />
          <FormsDropdown
            value={selectedForm}
            options={formsList}
            handleSelect={(val) => setSelectedForm(val)}
          />
        </div>
      </div>

      {isInitialLoading ? (
        <div className="loader--container">
          <div className="loader--"></div>
        </div>
      ) : (
        <div
          id="request-scrollable-container"
          className="request-main---container"
        >
          <InfiniteScroll
            dataLength={requests.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <div className="request-main__bottom-loader">
                <div className="loader-- small"></div>
                <span>Loading more requests...</span>
              </div>
            }
            endMessage={
              requests.length > 0 ? (
                <p className="request-main__end-message">
                  You’ve reached the end.
                </p>
              ) : null
            }
            scrollableTarget="request-scrollable-container"
            scrollThreshold="180px"
          >
            {requests.length > 0 ? (
              <div className="request-main__list">
                {requests.map((requestData) => (
                  <div className="request-main__item" key={requestData.id}>
                    <ReportItemList requestData={requestData} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="request-main__empty-state">
                <div className="request-main__empty-icon">📝</div>
                <h4>No requests found</h4>
                <p>
                  {searchKeyword
                    ? "Try changing your search keyword."
                    : "You don’t have any requests yet."}
                </p>
              </div>
            )}
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
}

export default ReportList;
